import React from 'react';
import { useCopperOxideStore } from '../store/useCopperOxideStore';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export function CopperOxideChart() {
  const results = useCopperOxideStore(s => s.results);
  const sorted = [...results].sort((a, b) => a.trialId - b.trialId);

  const data = {
    labels: sorted.map(r => `Trial ${r.trialId} (${r.cuOMassG.toFixed(1)} g)`),
    datasets: [
      {
        label: 'Mass of Cu (g)',
        data: sorted.map(r => r.cuMassG),
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
      },
      {
        label: 'Mass of O (g)',
        data: sorted.map(r => r.oxygenLostG),
        backgroundColor: 'rgba(245, 158, 11, 0.7)',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { stacked: true },
      y: { stacked: true, title: { display: true, text: 'Mass (g)' } },
    },
    plugins: {
      legend: { position: 'top' as const },
      title: { display: true, text: 'Mass of Cu and O in each trial' },
    },
  };

  const avgRatio = sorted.length > 0
    ? sorted.reduce((acc, r) => acc + r.ratioCuToO, 0) / sorted.length
    : 0;

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-lg font-bold mb-3">Empirical Formula Chart</h2>
      {sorted.length === 0 && (
        <p className="text-sm text-muted-foreground">Run trials to see the chart.</p>
      )}
      {sorted.length > 0 && (
        <>
          <div className="flex-1 min-h-[300px]">
            <Bar data={data} options={options} />
          </div>
          <div className="mt-4 p-3 rounded-lg bg-card border border-border text-sm">
            <p className="text-muted-foreground">
              Across {sorted.length} trial{sorted.length === 1 ? '' : 's'}, the
              average mole ratio of Cu to O is
              <span className="font-mono font-bold text-foreground"> 1 : {avgRatio.toFixed(2)}</span>.
              The bars scale linearly with the starting mass of CuO, but the
              ratio of O to Cu stays roughly constant — the visual proof that
              the empirical formula is independent of the starting amount.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default CopperOxideChart;
